import React, { useCallback, useEffect, useRef, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Warehouse,
  Search,
  Plus,
  Edit,
  Trash2,
  RotateCcw,
  History,
  XCircle,
  BarChart2,
  User,
  Clock,
  Send,
  CheckCircle,
  XOctagon,
  Package,
  ChevronDown,
  ChevronUp,
  Printer, // Added Printer icon
} from "lucide-react";
import { format } from "date-fns";
import Loader from "../../loader/Loader";
import AddWarehouseRequest from "./Addwarehouserequest";
import AuditInfoModal, { buildAuditRows } from "../../shared/AuditInfoModal";
import Pagination from "../Pagination";
import { domain } from "../../../security";
import { selectUserID } from "../../../redux/IchthusSlice";
import { useReactToPrint } from "react-to-print";
import {
  WarehouseRequestPrint,
  WarehouseRequestListPrint,
} from "./WarehouseRequestPrint";

const formatDate = (d) => (d ? format(new Date(d), "MMM dd, yyyy") : "—");

const STATUS_CONFIG = {
  Draft: { bg: "bg-slate-100", text: "text-slate-600", dot: "bg-slate-400" },
  Pending: { bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-400" },
  Approved: { bg: "bg-green-50", text: "text-green-700", dot: "bg-green-500" },
  Rejected: { bg: "bg-red-50", text: "text-red-600", dot: "bg-red-400" },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG["Draft"];
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wide ${cfg.bg} ${cfg.text}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {status}
    </span>
  );
};

// ── REJECT MODAL ──────────────────────────────────────────────────────────────
const RejectModal = ({ isOpen, onClose, onConfirm }) => {
  const [reason, setReason] = useState("");
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
        <h3 className="font-bold text-slate-800 text-base mb-1">
          Reject Request
        </h3>
        <p className="text-xs text-slate-400 mb-4">
          Provide a reason for rejection.
        </p>
        <textarea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason for rejection..."
          className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-red-100 focus:border-red-400 resize-none"
        />
        <div className="flex gap-2 mt-4">
          <button
            onClick={onClose}
            className="flex-1 py-2 text-sm font-bold text-slate-500 hover:bg-slate-50 rounded-xl border border-slate-200 transition"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onConfirm(reason);
              setReason("");
            }}
            disabled={!reason.trim()}
            className="flex-1 py-2 text-sm font-bold text-white bg-red-500 hover:bg-red-600 rounded-xl disabled:opacity-40 transition"
          >
            Confirm Reject
          </button>
        </div>
      </div>
    </div>
  );
};

// ── HISTORY MODAL ─────────────────────────────────────────────────────────────
const DeletedHistoryModal = ({ isOpen, onClose, onRestore }) => {
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${domain}/api/WarehouseRequests/history?search=${search}`,
      );
      setHistoryData(response.data);
    } catch {
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    if (isOpen) {
      fetchHistory();
      setCurrentPage(1);
    }
  }, [isOpen, fetchHistory]);

  if (!isOpen) return null;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = historyData.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex justify-between items-center">
          <h2 className="text-base font-bold text-slate-800 flex items-center gap-2">
            <div className="p-2 bg-red-50 rounded-full text-red-500">
              <Trash2 size={16} />
            </div>
            Deleted History
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition"
          >
            <XCircle size={20} />
          </button>
        </div>
        <div className="px-4 py-3 bg-slate-50/50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search history..."
              className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-red-100 focus:border-red-400 text-sm outline-none"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader />
            </div>
          ) : historyData.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-sm">
              No deleted records found.
            </div>
          ) : (
            <>
              <div className="hidden sm:block bg-white rounded-xl border border-slate-200 overflow-hidden">
                <table className="w-full text-sm text-left text-slate-600">
                  <thead className="text-xs text-slate-500 uppercase bg-slate-50">
                    <tr>
                      <th className="px-4 py-3">Request #</th>
                      <th className="px-4 py-3">Warehouse</th>
                      <th className="px-4 py-3">Deleted By</th>
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {currentItems.map((item) => (
                      <tr key={item.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-bold text-slate-800">
                          {item.requestNumber}
                        </td>
                        <td className="px-4 py-3 text-xs">
                          {item.warehouseName}
                        </td>
                        <td className="px-4 py-3 text-xs text-red-500 font-medium">
                          {item.deletedByFullName || "Unknown"}
                        </td>
                        <td className="px-4 py-3 text-xs text-red-500">
                          {formatDate(item.deletedDate)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={async () => {
                              await onRestore(item.id);
                              fetchHistory();
                            }}
                            className="text-green-600 hover:text-green-700 font-bold text-xs flex items-center justify-end gap-1 ml-auto"
                          >
                            <RotateCcw size={12} /> Restore
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-2">
                <Pagination
                  itemsPerPage={itemsPerPage}
                  totalItems={historyData.length}
                  currentPage={currentPage}
                  paginate={setCurrentPage}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ── EXPANDED ROW (line items) ─────────────────────────────────────────────────
const ExpandedItems = ({ items }) => (
  <div className="px-4 md:px-6 pb-4 pt-2">
    <div className="bg-slate-50 rounded-xl border border-slate-100 overflow-x-auto">
      <table className="w-full min-w-[400px] text-xs">
        <thead>
          <tr className="bg-slate-100 text-slate-500 uppercase tracking-wide">
            <th className="px-4 py-2 text-left">#</th>
            <th className="px-4 py-2 text-left">Product</th>
            <th className="px-4 py-2 text-right">Requested Qty</th>
            <th className="px-4 py-2 text-right">Approved Qty</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {(items || []).map((item, i) => (
            <tr key={i} className="hover:bg-white transition">
              <td className="px-4 py-2 text-slate-400 font-bold">{i + 1}</td>
              <td className="px-4 py-2 font-semibold text-slate-700">
                {item.productName}
                {item.itemCode && (
                  <span className="ml-1 text-slate-400 font-normal">
                    ({item.itemCode})
                  </span>
                )}
              </td>
              <td className="px-4 py-2 text-right font-bold text-slate-700">
                {item.requestedQuantity}
              </td>
              <td className="px-4 py-2 text-right font-bold text-green-600">
                {item.approvedQuantity ?? "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
const AllWarehouseRequests = () => {
  const currentUserID = useSelector(selectUserID);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requestToEdit, setRequestToEdit] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
  const [auditItem, setAuditItem] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  // Single Print Setup
  const singlePrintRef = useRef(null);
  const [printTarget, setPrintTarget] = useState(null);
  const handleSinglePrint = useReactToPrint({
    content: () => singlePrintRef.current,
  });

  const triggerSinglePrint = (item) => {
    setPrintTarget(item);
    setTimeout(() => handleSinglePrint(), 200);
  };

  // General Report Print Setup
  const listPrintRef = useRef(null);
  const handleListPrint = useReactToPrint({
    content: () => listPrintRef.current,
  });

  const triggerListPrint = () => {
    setTimeout(() => handleListPrint(), 200);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${domain}/api/WarehouseRequests`);
      setData(res.data);
    } catch {
      toast.error("Failed to fetch Warehouse Requests.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = data.filter((item) => {
    const matchSearch =
      (item.requestNumber || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (item.warehouseName || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (item.requestedByFullName || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter === "All" || item.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filtered.slice(indexOfFirstItem, indexOfLastItem);

  const toggleExpand = (id) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleSubmitForApproval = async (id) => {
    if (!window.confirm("Submit this request for approval?")) return;
    try {
      await axios.put(`${domain}/api/WarehouseRequests/submit/${id}`, {
        userID: currentUserID,
      });
      toast.success("Request submitted for approval!");
      fetchData();
    } catch {
      toast.error("Failed to submit request.");
    }
  };

  const handleApprove = async (id) => {
    if (!window.confirm("Approve this request? Stock will be updated.")) return;
    try {
      await axios.put(`${domain}/api/WarehouseRequests/approve/${id}`, {
        userID: currentUserID,
      });
      toast.success("Request approved! Warehouse stock updated.");
      fetchData();
    } catch {
      toast.error("Failed to approve request.");
    }
  };

  const handleReject = async (reason) => {
    if (!rejectTarget) return;
    try {
      await axios.put(
        `${domain}/api/WarehouseRequests/reject/${rejectTarget}`,
        {
          userID: currentUserID,
          reason,
        },
      );
      toast.success("Request rejected.");
      setRejectTarget(null);
      fetchData();
    } catch {
      toast.error("Failed to reject request.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this request?")) return;
    try {
      await axios.put(`${domain}/api/WarehouseRequests/soft-delete/${id}`, {
        userID: currentUserID,
      });
      toast.success("Request moved to history.");
      fetchData();
    } catch {
      toast.error("Failed to delete request.");
    }
  };

  const handleRestore = async (id) => {
    try {
      await axios.put(`${domain}/api/WarehouseRequests/restore/${id}`, {
        userID: currentUserID,
      });
      toast.success("Request restored!");
      fetchData();
    } catch {
      toast.error("Failed to restore.");
    }
  };

  const statuses = ["All", "Draft", "Pending", "Approved", "Rejected"];

  // ── UNIFIED ACTION BUTTONS RENDERER (Dry & Responsive) ────────────────────
  const renderActions = (item, isDesktop) => {
    const btnClass = isDesktop
      ? "px-2 py-1 text-xs font-medium rounded-md transition flex items-center gap-1"
      : "text-[11px] font-bold flex items-center justify-center gap-1 flex-1 py-1.5";

    return (
      <>
        {/* ✨ ONLY RENDER PRINT BUTTON IF STATUS IS APPROVED ✨ */}
        {item.status === "Approved" && (
          <button
            onClick={() => triggerSinglePrint(item)}
            className={`${btnClass} ${
              isDesktop
                ? "text-slate-500 hover:text-blue-600 hover:bg-blue-50"
                : "text-blue-600 bg-blue-50"
            }`}
            title="Print Request"
          >
            <Printer size={isDesktop ? 14 : 12} />
            {!isDesktop && <span>Print</span>}
          </button>
        )}

        {isDesktop && (
          <button
            onClick={() => setAuditItem(item)}
            className={`${btnClass} text-slate-500 hover:text-amber-600 hover:bg-amber-50`}
            title="Audit"
          >
            <Clock size={14} />
          </button>
        )}

        {item.status === "Draft" && (
          <>
            <button
              onClick={() => handleSubmitForApproval(item.id)}
              className={`${btnClass} ${isDesktop ? "text-indigo-600 hover:bg-indigo-50" : "text-indigo-600 bg-indigo-50"}`}
            >
              <Send size={isDesktop ? 14 : 12} />
              <span>{isDesktop ? "Submit" : "Submit For Approval"}</span>
            </button>
            <button
              onClick={() => {
                setRequestToEdit(item);
                setIsAddModalVisible(true);
              }}
              className={`${btnClass} ${isDesktop ? "text-slate-600 hover:text-indigo-600 hover:bg-indigo-50" : "text-slate-600 bg-slate-100"}`}
            >
              <Edit size={isDesktop ? 14 : 12} />
            </button>
            <button
              onClick={() => handleDelete(item.id)}
              className={`${btnClass} ${isDesktop ? "text-red-500 hover:text-red-600 hover:bg-red-50" : "text-red-600 bg-red-50"}`}
            >
              <Trash2 size={isDesktop ? 14 : 12} />
            </button>
          </>
        )}

        {item.status === "Pending" && item.approverUserId === currentUserID && (
          <>
            <button
              onClick={() => handleApprove(item.id)}
              className={`${btnClass} ${isDesktop ? "text-green-600 hover:bg-green-50" : "text-green-600 bg-green-50"}`}
            >
              <CheckCircle size={isDesktop ? 14 : 12} />
              <span>Approve</span>
            </button>
            <button
              onClick={() => setRejectTarget(item.id)}
              className={`${btnClass} ${isDesktop ? "text-red-500 hover:text-red-600 hover:bg-red-50" : "text-red-600 bg-red-50"}`}
            >
              <XOctagon size={isDesktop ? 14 : 12} />
              <span>Reject</span>
            </button>
          </>
        )}

        {item.status === "Pending" && item.approverUserId !== currentUserID && (
          <span
            className={`text-[10px] font-bold text-slate-400 uppercase bg-slate-50 px-2 py-1 rounded-md ${isDesktop ? "" : "ml-auto"}`}
          >
            Awaiting Approval
          </span>
        )}
      </>
    );
  };

  return (
    <div className="min-h-screen pb-20">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* HIDDEN PRINT TARGETS */}
      <div className="hidden">
        {printTarget && (
          <WarehouseRequestPrint ref={singlePrintRef} data={printTarget} />
        )}
        <WarehouseRequestListPrint
          ref={listPrintRef}
          dataList={filtered}
          filters={`Search: ${searchTerm || "None"} | Status: ${statusFilter}`}
        />
      </div>

      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-slate-800 flex items-center gap-2">
                <Warehouse className="text-indigo-600" /> Warehouse Requests
              </h1>
              <p className="text-xs md:text-sm text-slate-500 mt-1 hidden sm:block">
                Request and approve stock transfers into warehouses.
              </p>
            </div>
            {/* Action Buttons Wrap */}
            <div className="flex flex-wrap gap-3 w-full md:w-auto">
              {/* PRINT SUMMARY BUTTON */}
              <button
                onClick={triggerListPrint}
                className="flex-1 md:flex-none flex justify-center items-center gap-2 bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm font-medium px-4 py-2.5 rounded-xl transition-all"
              >
                <Printer size={18} /> Print Summary
              </button>

              <button
                onClick={() => setIsHistoryModalVisible(true)}
                className="flex-1 md:flex-none flex justify-center items-center gap-2 bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100 text-sm font-medium px-4 py-2.5 rounded-xl transition-all"
              >
                <History size={18} /> History
              </button>
              <button
                onClick={() => {
                  setRequestToEdit(null);
                  setIsAddModalVisible(true);
                }}
                className="flex-1 md:flex-none flex justify-center items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-5 py-2.5 rounded-xl shadow-md shadow-indigo-200 transition-all"
              >
                <Plus size={18} /> New Request
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-4 px-4">
        {/* Stats + Search */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex items-center gap-4">
            <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
              <BarChart2 size={22} />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                Total Requests
              </p>
              <p className="text-2xl font-bold text-slate-800">
                {filtered.length}
              </p>
            </div>
          </div>
          <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex items-center gap-4">
            <div className="p-3 bg-amber-50 text-amber-600 rounded-xl">
              <Clock size={22} />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                Pending Approval
              </p>
              <p className="text-2xl font-bold text-slate-800">
                {data.filter((d) => d.status === "Pending").length}
              </p>
            </div>
          </div>
          <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex items-center">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search requests..."
                className="w-full pl-9 pr-4 py-2 text-sm border-none ring-1 ring-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none placeholder:text-slate-400"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Status Filter */}
        <div className="flex gap-2 mb-4 flex-wrap">
          {statuses.map((s) => (
            <button
              key={s}
              onClick={() => {
                setStatusFilter(s);
                setCurrentPage(1);
              }}
              className={`px-4 py-1.5 rounded-xl text-xs font-bold transition-all ${statusFilter === s ? "bg-indigo-600 text-white shadow-md shadow-indigo-200" : "bg-white text-slate-500 border border-slate-200 hover:bg-slate-50"}`}
            >
              {s}
              {s !== "All" && (
                <span className="ml-1.5 opacity-60">
                  {data.filter((d) => d.status === s).length}
                </span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader />
          </div>
        ) : (
          /* ── UNIFIED RESPONSIVE GRID ───────────────────────────────────── */
          <div className="flex flex-col gap-4 md:gap-0 md:bg-white md:rounded-2xl md:shadow-sm md:border md:border-slate-200 md:overflow-hidden">
            {/* Desktop Header Row */}
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 bg-slate-50 border-b border-slate-200 text-xs text-slate-500 uppercase font-semibold">
              <div className="col-span-1"></div>
              <div className="col-span-2">Request #</div>
              <div className="col-span-2">Warehouse</div>
              <div className="col-span-2">Requested By</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-1">Date</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>

            {/* List Loop */}
            {currentItems.map((item) => (
              <React.Fragment key={item.id}>
                {/* Single Row / Card Wrapper */}
                <div className="bg-white rounded-2xl md:rounded-none p-4 md:p-0 shadow-sm border border-slate-100 md:border-0 md:border-b md:last:border-none hover:bg-slate-50 transition">
                  {/* --- DESKTOP VIEW --- */}
                  <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 items-center">
                    <div className="col-span-1">
                      <button
                        onClick={() => toggleExpand(item.id)}
                        className="p-1.5 text-slate-400 hover:text-indigo-500 hover:bg-indigo-50 rounded-lg"
                      >
                        {expandedRows.has(item.id) ? (
                          <ChevronUp size={14} />
                        ) : (
                          <ChevronDown size={14} />
                        )}
                      </button>
                    </div>
                    <div className="col-span-2 font-bold text-slate-800">
                      {item.requestNumber}
                    </div>
                    <div className="col-span-2 font-medium text-slate-700">
                      {item.warehouseName}
                    </div>
                    <div className="col-span-2 flex flex-col">
                      <span className="font-semibold text-slate-700">
                        {item.requestedByFullName || "—"}
                      </span>
                      <span className="text-xs text-slate-400">
                        {formatDate(item.requestDate)}
                      </span>
                    </div>
                    <div className="col-span-2">
                      <StatusBadge status={item.status} />
                    </div>
                    <div className="col-span-1 text-xs text-slate-500">
                      {formatDate(item.createdDate)}
                    </div>
                    <div className="col-span-2 flex justify-end gap-1.5">
                      {renderActions(item, true)}
                    </div>
                  </div>

                  {/* --- MOBILE VIEW --- */}
                  <div className="md:hidden flex flex-col">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-bold text-slate-800">
                          {item.requestNumber}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {item.warehouseName}
                        </p>
                      </div>
                      <StatusBadge status={item.status} />
                    </div>

                    <div className="flex items-center gap-2 text-xs text-slate-400 mb-3">
                      <User size={11} /> {item.requestedByFullName || "—"} ·{" "}
                      {formatDate(item.requestDate)}
                    </div>

                    <div className="flex items-center flex-wrap gap-2 pt-3 border-t border-slate-50">
                      <button
                        onClick={() => toggleExpand(item.id)}
                        className="flex-1 min-w-[100px] text-xs font-bold text-indigo-600 bg-indigo-50 py-1.5 rounded-lg flex items-center justify-center gap-1"
                      >
                        <Package size={12} />{" "}
                        {expandedRows.has(item.id) ? "Hide" : "View"}
                      </button>
                      {renderActions(item, false)}
                    </div>
                  </div>
                </div>

                {/* Expanded Line Items */}
                {expandedRows.has(item.id) && (
                  <div className="bg-slate-50/60 md:border-b border-slate-100">
                    <ExpandedItems items={item.items} />
                  </div>
                )}
              </React.Fragment>
            ))}

            {currentItems.length === 0 && (
              <div className="text-center py-10 text-slate-400 bg-white md:bg-transparent rounded-2xl border border-slate-100 md:border-none">
                No requests found.
              </div>
            )}
          </div>
        )}

        <div className="mt-4 pb-6">
          <Pagination
            itemsPerPage={itemsPerPage}
            totalItems={filtered.length}
            currentPage={currentPage}
            paginate={setCurrentPage}
          />
        </div>
      </div>

      {/* Add/Edit Modal */}
      {isAddModalVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden">
            <AddWarehouseRequest
              onClose={() => {
                setIsAddModalVisible(false);
                fetchData();
              }}
              refreshData={fetchData}
              requestToEdit={requestToEdit}
            />
          </div>
        </div>
      )}

      <DeletedHistoryModal
        isOpen={isHistoryModalVisible}
        onClose={() => {
          setIsHistoryModalVisible(false);
          fetchData();
        }}
        onRestore={handleRestore}
      />
      <RejectModal
        isOpen={!!rejectTarget}
        onClose={() => setRejectTarget(null)}
        onConfirm={handleReject}
      />
      <AuditInfoModal
        isOpen={!!auditItem}
        onClose={() => setAuditItem(null)}
        title={auditItem?.requestNumber}
        rows={buildAuditRows(auditItem)}
      />
    </div>
  );
};

export default AllWarehouseRequests;
